import React, { FC } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Alert, Button, Card, CardBody, Col, Row } from 'reactstrap';
import {
  getReleasePeriod,
  HotelReleaseGroupEntries,
  ReleaseForceEntry,
  ReleaseGroupStage,
  ReleaseGroupType,
} from '../../../shared/hotel';
import {
  LinkButton,
  LoginRedirect,
  PageHeader,
  RichMarkdownIt,
  UserStateComponent,
} from '../../components';
import { MaterialIconText } from '../../components/MaterialIconText';
import { useTranslation } from '../../translations';
import { displayName, Fetcher, isResourceError, useFetcher, useUser } from '../../utils';
import { captureError } from '../../utils/errorHandling';
import { WaveEligibility } from './WaveEligibility';
import { WaveEntry } from './WaveEntry';
import { WaveStatus } from './WaveStatus';
import { WaveEarlyEntryPeriodAlert } from './util';

import './WaveInfo.scss';

export const WaveInfo: FC = () => {
  const user = useUser();
  const history = useHistory();

  const {
    params: { id },
  } = useRouteMatch<{ id: string }>();

  const waveInfo = useFetcher(async () => {
    try {
      return await api.getPublicHotelReleaseById(id);
    } catch (error) {
      if (!isResourceError(error, 'ReleaseGroup')) {
        // If it's not an ReleaseGroup ResourceError, then there's a bigger issue
        captureError(error as Error);
        return;
      }

      throw error;
    }
  }, [id]);

  const entryFetcher = useFetcher<HotelReleaseGroupEntries | null>(async () => {
    try {
      return await api.getReleaseEntry(id);
    } catch (error) {
      if (isResourceError(error, 'ReleaseEntry')) {
        return null;
      }

      throw error;
    }
  }, [id]);

  const { ts } = useTranslation();

  // If not logged in, redirect to login page
  if (!user) {
    return <LoginRedirect />;
  }

  // If we're still fetching the wave info, show a loading state
  if (!waveInfo.success) {
    return <Fetcher inline result={waveInfo} />;
  }

  if (!waveInfo.data) {
    toast.error(ts('failed_to_load_wave_information', id));
    history.push('/hotel');
    return null;
  }

  const wave = waveInfo.data;
  const entry = entryFetcher.data;
  const isEntryReady = !entryFetcher.loading;

  return (
    <UserStateComponent>
      <PageHeader>
        {ts('hotel')} &mdash; {displayName(wave)}
      </PageHeader>
      <Row>
        <Col md={5} xs={12}>
          <div className="mb-3">
            <LinkButton block color="secondary" outline to="/hotel">
              {ts('return_to_wave_list')}
            </LinkButton>
          </div>
          {wave.showRoomAvailability && (wave.rooms?.length ?? 0) > 0 && (
            <Card className="mb-3">
              <CardBody>
                <Button block color="primary" outline>
                  {ts('view_available_inventory')}
                </Button>
                <hr />
                <p className="mb-0">
                  <MaterialIconText name="info">
                    <small>
                      {ts('available_hotel_inventory_shows_a_snapshot_of_the_rooms_available')}
                    </small>
                  </MaterialIconText>
                </p>
              </CardBody>
            </Card>
          )}
          <Card className="mb-3">
            <CardBody>
              <h4>{displayName(wave)}</h4>
              <ul className="list-unstyled">
                <li>
                  <strong>{ts('wave_type')}</strong>:{' '}
                  {wave.type === ReleaseGroupType.FirstComeFirstServe
                    ? 'First Come, First Served (FCFS)'
                    : 'Random Drawing'}
                </li>
              </ul>
              {wave.description && (
                <>
                  <hr />
                  <RichMarkdownIt source={wave.description} />
                </>
              )}
            </CardBody>
          </Card>
          {!isEntryReady || entry ? null : (
            <Card className="mb-3">
              <CardBody>
                {wave.forceEntry !== ReleaseForceEntry.None && (
                  <Card className="mb-3">
                    <CardBody>
                      <Alert color="info" fade={false}>
                        <p>
                          <strong>{ts('this_wave_uses_an_automatic_entry_system')}.</strong>{' '}
                          {ts('hotel.wave.auto_entry')}
                        </p>
                      </Alert>
                    </CardBody>
                  </Card>
                )}
                {/* TODO: Hide behind configuration */}
                <WaveEligibility wave={wave} />
              </CardBody>
            </Card>
          )}
        </Col>
        <Col md={7} xs={12}>
          <Card className="mb-3">
            <CardBody>
              <WaveStatus wave={wave} />
            </CardBody>
          </Card>
          {wave.type === ReleaseGroupType.FirstComeFirstServe &&
            getReleasePeriod(wave, new Date()) === ReleaseGroupStage.EntryPeriod && (
              <WaveEarlyEntryPeriodAlert wave={wave} />
            )}
          {entryFetcher.success ? (
            <WaveEntry entry={entryFetcher.data} refresh={entryFetcher.refresh} wave={wave} />
          ) : (
            <Fetcher inline result={entryFetcher} />
          )}
        </Col>
      </Row>
    </UserStateComponent>
  );
};
